import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api/api.js';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  ExternalLink, 
  MoreVertical, 
  FileText, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Eye,
  Trash,
  X,
  Check,
  Tag,
  Layers,
  Sparkles
} from 'lucide-react';
import { PostEditor } from './PostEditor.js';
import { CategoryManager, getCategoryBadgeStyle, getCategoryDotStyle } from './CategoryManager.js';
import { type Post, type BlogCategory } from '../api/blogApi.js';
import { ConfirmDeleteModal } from '../../../shared/components/ConfirmDeleteModal.js';

export const BlogManager: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'posts' | 'categories'>('posts');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);

  const { data: categories } = useQuery({
    queryKey: ['blog-categories'],
    queryFn: () => api.getBlogCategories(),
  });

  const { data: posts, isLoading } = useQuery({
    queryKey: ['admin-blog-posts', search, statusFilter],
    queryFn: () => api.getAdminPosts(search, statusFilter === 'all' ? undefined : statusFilter),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deletePost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-categories'] });
      setDeletingPostId(null);
    },
    onError: () => {
      setDeletingPostId(null);
    },
  });

  const handleDelete = (id: string) => {
    setDeletingPostId(id);
  };

  const openCreateEditor = () => {
    setEditingPost(null);
    setIsEditorOpen(true);
  };

  const openEditEditor = (post: Post) => {
    setEditingPost(post);
    setIsEditorOpen(true);
  };

  // Filter posts by category locally if selected
  const displayedPosts = React.useMemo(() => {
    if (!posts) return [];
    if (categoryFilter === 'all') return posts;
    return posts.filter((p) => {
      if (p.categoryId && p.categoryId === categoryFilter) return true;
      if (p.category && p.category.toLowerCase() === categoryFilter.toLowerCase()) return true;
      return false;
    });
  }, [posts, categoryFilter]);

  return (
    <div className="space-y-6">
      {/* Top Header & Navigation Tabs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shadow-inner">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Pengelolaan Blog & Konten</h2>
            <p className="text-sm text-slate-500">Buat artikel edukasi, publikasi wawasan, dan kelola kategori topik.</p>
          </div>
        </div>

        {/* Tab Switcher & Quick Action */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center p-1 bg-slate-100/80 rounded-2xl border border-slate-200/60">
            <button
              onClick={() => setActiveTab('posts')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                activeTab === 'posts'
                  ? 'bg-white text-indigo-600 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span>Daftar Artikel</span>
              {posts && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono ${activeTab === 'posts' ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-200 text-slate-600'}`}>
                  {posts.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                activeTab === 'categories'
                  ? 'bg-white text-indigo-600 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Layers className="w-4 h-4" />
              <span>Kelola Kategori</span>
              {categories && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono ${activeTab === 'categories' ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-200 text-slate-600'}`}>
                  {categories.length}
                </span>
              )}
            </button>
          </div>

          {activeTab === 'posts' && (
            <button 
              onClick={openCreateEditor}
              className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white font-bold text-sm shadow-lg shadow-indigo-500/20 hover:bg-indigo-700 transition-all flex items-center gap-2 cursor-pointer shrink-0"
            >
              <Plus className="w-4 h-4" />
              Artikel Baru
            </button>
          )}
        </div>
      </div>

      {/* Main Tab Content */}
      {activeTab === 'categories' ? (
        <CategoryManager />
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input 
                type="text" 
                placeholder="Cari artikel..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all shadow-xs"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select 
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-medium focus:outline-none focus:border-indigo-500 transition-colors shadow-xs"
              >
                <option value="all">Semua Kategori</option>
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>

              <select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-medium focus:outline-none focus:border-indigo-500 transition-colors shadow-xs"
              >
                <option value="all">Semua Status</option>
                <option value="published">Diterbitkan</option>
                <option value="draft">Draft / Rancangan</option>
              </select>
            </div>
          </div>

          {/* Posts Table */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/50 border-b border-slate-100">
                    <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Informasi Artikel</th>
                    <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Kategori</th>
                    <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Dibuat Pada</th>
                    <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {isLoading ? (
                    [1, 2, 3].map((i) => (
                      <tr key={`blog-manager-skel-${i}`} className="animate-pulse">
                        <td className="px-6 py-6" colSpan={5}>
                          <div className="h-4 bg-slate-50 rounded w-full" />
                        </td>
                      </tr>
                    ))
                  ) : displayedPosts && displayedPosts.length > 0 ? (
                    displayedPosts.map((post, pIdx) => {
                      const matchedCat = categories?.find(
                        (c) => c.id === post.categoryId || c.name.toLowerCase() === (post.category || '').toLowerCase()
                      );

                      return (
                        <tr key={`blog-row-${post.id || post.slug}-${pIdx}`} className="hover:bg-slate-50/80 transition-colors group">
                          <td className="px-6 py-5">
                            <div className="flex flex-col max-w-md">
                              <span className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-1">
                                {post.title}
                              </span>
                              <span className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                                <Eye className="w-3 h-3" />
                                /blog/{post.slug}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            {post.category ? (
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold ${getCategoryBadgeStyle(
                                  matchedCat?.color
                                )}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${getCategoryDotStyle(matchedCat?.color)}`} />
                                {post.category}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400 italic">Tanpa Kategori</span>
                            )}
                          </td>
                          <td className="px-6 py-5">
                            {post.status === 'published' ? (
                              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px] font-bold uppercase tracking-wider">
                                <CheckCircle className="w-3 h-3" />
                                Diterbitkan
                              </div>
                            ) : (
                              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 border border-amber-100 text-[10px] font-bold uppercase tracking-wider">
                                <Clock className="w-3 h-3" />
                                Draft
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex flex-col">
                              <span className="text-xs font-medium text-slate-700">
                                {new Date(post.createdAt).toLocaleDateString('id-ID', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </span>
                              <span className="text-[10px] text-slate-400 font-mono mt-0.5">
                                {new Date(post.createdAt).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-5 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button 
                                onClick={() => openEditEditor(post)}
                                className="p-2 rounded-xl hover:bg-white hover:text-indigo-600 text-slate-400 hover:shadow-sm border border-transparent hover:border-slate-200 transition-all cursor-pointer"
                                title="Edit Artikel"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <a 
                                href={`/blog/${post.slug}`}
                                target="_blank"
                                rel="noreferrer"
                                className="p-2 rounded-xl hover:bg-white hover:text-blue-600 text-slate-400 hover:shadow-sm border border-transparent hover:border-slate-200 transition-all cursor-pointer"
                                title="Lihat Publik"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                              <button 
                                onClick={() => handleDelete(post.id)}
                                disabled={deleteMutation.isPending}
                                className="p-2 rounded-xl hover:bg-white hover:text-rose-600 text-slate-400 hover:shadow-sm border border-transparent hover:border-slate-200 transition-all cursor-pointer"
                                title="Hapus Artikel"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <div className="w-16 h-16 rounded-3xl bg-slate-50 flex items-center justify-center text-slate-300">
                            <FileText className="w-8 h-8" />
                          </div>
                          <p className="text-slate-500 text-sm font-medium">Belum ada artikel yang sesuai kriteria.</p>
                          <button 
                            onClick={openCreateEditor}
                            className="text-indigo-600 text-sm font-bold hover:underline cursor-pointer"
                          >
                            Buat artikel baru sekarang
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Post Editor Modal */}
      {isEditorOpen && (
        <PostEditor 
          post={editingPost} 
          isOpen={isEditorOpen} 
          onClose={() => setIsEditorOpen(false)} 
        />
      )}

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={Boolean(deletingPostId)}
        title="Hapus Artikel Blog"
        description="Apakah Anda yakin ingin menghapus artikel ini secara permanen?"
        confirmText="Hapus Artikel"
        isPending={deleteMutation.isPending}
        onClose={() => setDeletingPostId(null)}
        onConfirm={() => {
          if (deletingPostId) {
            deleteMutation.mutate(deletingPostId);
          }
        }}
      />
    </div>
  );
};
